/**
 * AppRoutes - Dynamic route rendering from route config
 * Uses lazy loading for code splitting
 */

import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts';
import MainLayout from '@/layouts/MainLayout';
import PageGuard from '@/components/guards/PageGuard';
import { LoadingSpinner } from '@/components/ui';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import {
  publicRoutes,
  allProtectedRoutes,
  redirectRoutes,
  type RouteConfig,
} from './config';

/**
 * Loading fallback for lazy-loaded components
 */
const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <LoadingSpinner size="lg" />
  </div>
);

/**
 * Redirect component that determines the user's dashboard based on their roles
 */
export const RoleDashboardRedirect: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  const roles = user.availableRoles || [user.role];

  let to = '/guest/dashboard';

  if (roles.includes('admin')) {
    to = '/admin/dashboard';
  } else if (roles.includes('lecturer')) {
    to = '/lecturer/dashboard';
  } else if (roles.includes('staff')) {
    to = '/staff/dashboard';
  } else if (roles.includes('student')) {
    to = '/student/dashboard';
  } else if (roles.includes('alumni')) {
    to = '/alumni/dashboard';
  } else if (roles.includes('student_applicant')) {
    to = '/applicant/dashboard';
  } else if (roles.includes('guest')) {
    to = '/guest/dashboard';
  }

  return <Navigate to={to} replace />;
};

/**
 * Renders a protected route with layout and guard
 */
const ProtectedRoute: React.FC<{ config: RouteConfig }> = ({ config }) => {
  const Component = config.component;

  return (
    <MainLayout>
      <PageGuard isEnabled={config.isEnabled}>
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Component />
          </Suspense>
        </ErrorBoundary>
      </PageGuard>
    </MainLayout>
  );
};

/**
 * Renders a public route without layout
 */
const PublicRoute: React.FC<{ config: RouteConfig }> = ({ config }) => {
  const Component = config.component;

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Component />
      </Suspense>
    </ErrorBoundary>
  );
};

/**
 * Main application routes component
 * Dynamically renders all routes from the route configuration
 */
export const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public routes */}
      {publicRoutes
        .filter((route) => route.isEnabled)
        .map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={<PublicRoute config={route} />}
          />
        ))}

      {/* Protected routes */}
      {allProtectedRoutes
        .filter((route) => route.isEnabled)
        .map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={<ProtectedRoute config={route} />}
          />
        ))}

      {/* Redirect routes */}
      {redirectRoutes.map((redirect) => (
        <Route
          key={redirect.from}
          path={redirect.from}
          element={
            redirect.to === '/dashboard' ? (
              <RoleDashboardRedirect />
            ) : (
              <Navigate to={redirect.to} replace />
            )
          }
        />
      ))}

      {/* Dashboard redirect (handles /dashboard path) */}
      <Route path="/dashboard" element={<RoleDashboardRedirect />} />

      {/* Catch-all redirect to dashboard */}
      <Route path="*" element={<RoleDashboardRedirect />} />
    </Routes>
  );
};

export default AppRoutes;
